import { useEffect, useState } from 'react';
import { CreditCard, Check, Clock } from 'lucide-react';
import { superFetch, formatCurrency } from '@/lib/super-admin-api';
import type { Plan } from '@/lib/super-admin-types';

export function PlansPage() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchPlans() {
      try {
        const res = await superFetch('/api/super-admin/plans');
        if (res.ok) {
          const data = await res.json();
          setPlans(Array.isArray(data) ? data : []);
        }
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    }
    fetchPlans();
  }, []);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold" style={{ color: '#f1f5f9' }}>Planos</h1>
        <p className="text-base mt-1" style={{ color: '#94a3b8' }}>
          Gerencie os planos disponíveis para os estabelecimentos
        </p>
      </div>

      {/* Plans Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="animate-pulse rounded-xl p-6" style={{ background: '#1e293b' }}>
              <div className="h-48" />
            </div>
          ))}
        </div>
      ) : plans.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 rounded-xl" style={{ background: '#1e293b' }}>
          <CreditCard className="h-12 w-12 mb-3" style={{ color: '#475569' }} />
          <p className="text-base" style={{ color: '#94a3b8' }}>Nenhum plano cadastrado</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((plan, index) => {
            const isPopular = index === 1; // Middle plan highlighted
            const borderColor = isPopular ? '#10b981' : '#334155';
            const features = Array.isArray(plan.features)
              ? plan.features
              : typeof plan.features === 'string'
                ? JSON.parse(plan.features || '[]')
                : [];

            return (
              <div
                key={plan.id}
                className="rounded-xl border p-6 flex flex-col relative"
                style={{
                  background: '#1e293b',
                  borderColor,
                  borderWidth: isPopular ? 2 : 1,
                }}
              >
                {isPopular && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-0.5 text-xs font-bold text-white"
                    style={{ background: '#10b981' }}
                  >
                    POPULAR
                  </div>
                )}

                {/* Plan Header */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-bold" style={{ color: '#f1f5f9' }}>{plan.name}</h3>
                    <span
                      className="rounded-full px-2 py-0.5 text-xs font-medium"
                      style={{
                        background: plan.active ? '#10b98120' : '#64748b20',
                        color: plan.active ? '#10b981' : '#94a3b8',
                      }}
                    >
                      {plan.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold" style={{ color: '#f1f5f9' }}>
                      {formatCurrency(plan.price)}
                    </span>
                    <span className="text-base" style={{ color: '#64748b' }}>/mês</span>
                  </div>
                </div>

                {/* Features */}
                <div className="flex-1 space-y-3 mb-6">
                  {features.map((feature: string, i: number) => (
                    <div key={i} className="flex items-start gap-2">
                      <Check className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: '#10b981' }} />
                      <span className="text-sm" style={{ color: '#94a3b8' }}>{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Grace Period */}
                <div
                  className="flex items-center gap-2 rounded-lg px-3 py-2"
                  style={{ background: '#0f172a' }}
                >
                  <Clock className="h-4 w-4" style={{ color: '#f59e0b' }} />
                  <span className="text-sm" style={{ color: '#94a3b8' }}>
                    {plan.grace_period_days} dias de carência
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
